import { Header, Footer } from '@/shared/components/layout';
import { useDocumentTitle } from '@/shared/hooks';
import { Hero24, ContentSection01 } from '@/features/Brokerages/Components';
import { ContentSection02 } from './Components';
import { TryItFree } from '@/features/Pricing/Components/TryItFree';

const HowItWorksPage = () => {
  useDocumentTitle(
    'How It Works — REALTOR.ca Showing-Request Leads | BrokerMax'
  );
  return (
    <div className="page font--jakarta">
      <Header variant="light" />
      <main>
        <Hero24
          badgeLabel="✨ Individual leads · Shared or exclusive"
          title="How buying REALTOR.ca showing-request leads works"
          description="Set your territory, fund your wallet, and purchase high-intent buyer and rental leads from real Request a Showing activity — not popup traffic or forced forms. Choose shared or exclusive delivery where offered."
          backgroundImage="/images/hero-24.jpg"
        />
        <div className="container mx-auto max-w-7xl py-12 px-4">
          <div className="relative overflow-hidden">
            {/* Scrolling container */}
            <div className="flex items-center gap-8 opacity-30 partner-scroll whitespace-nowrap">
              {/* First set of logos */}
              {[
                {
                  src: '/images/realtor-canada.png',
                  alt: 'REALTOR.ca'
                },
                { src: '/images/brokerbay.png', alt: 'BrokerBay' },
                { src: '/images/canada.jpg', alt: 'Canada' },
                {
                  src: '/images/proptx.png',
                  alt: 'PropTx',
                  padding: '10px'
                },
                {
                  src: '/images/crea.png',
                  alt: 'CREA',
                  padding: '0px'
                }
              ].map((logo, i) => (
                <img
                  key={`first-${i}`}
                  src={logo.src}
                  alt={logo.alt}
                  className="h-12 object-contain flex-shrink-0"
                  style={{ padding: logo.padding }}
                />
              ))}
              {/* Duplicate set for seamless loop */}
              {[
                {
                  src: '/images/realtor-canada.png',
                  alt: 'REALTOR.ca'
                },
                { src: '/images/brokerbay.png', alt: 'BrokerBay' },
                { src: '/images/canada.jpg', alt: 'Canada' },
                {
                  src: '/images/proptx.png',
                  alt: 'PropTx',
                  padding: '10px'
                },
                {
                  src: '/images/crea.png',
                  alt: 'CREA',
                  padding: '0px'
                }
              ].map((logo, i) => (
                <img
                  key={`second-${i}`}
                  src={logo.src}
                  alt={logo.alt}
                  className="h-12 object-contain flex-shrink-0"
                  style={{ padding: logo.padding }}
                />
              ))}
            </div>
          </div>
          <style>{`
            @keyframes partnerScroll {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }
            .partner-scroll {
              animation: partnerScroll 20s linear infinite;
              display: flex;
              width: max-content;
            }
            @media (max-width: 768px) {
              .partner-scroll {
                animation: partnerScroll 8s linear infinite;
              }
            }
          `}</style>
          {/* END BADGES */}
        </div>
        <ContentSection01
          sectionId="lnk-1"
          sectionIdText="Step 1"
          title="Set your territory and wallet"
          description="Define where you work — postal FSAs and radius — so only relevant REALTOR.ca showing requests match your market. Add funds so you can purchase leads as they appear (or enable auto-purchase when you're ready)."
          subtitle="What you configure"
          items={[
            'Buyer and rental (lease) lead types matching your business',
            'Territory overlap so you compete where you actually serve clients',
            'Optional auto-purchase for fresh or clearance inventory when balance allows'
          ]}
          image="/images/showings.png"
          imageAlt="REALTOR.ca showing request workflow"
          imageFirst={false}
          buttonText="View pricing"
          buttonLink="/pricing"
        />
        <ContentSection01
          sectionId="lnk-2"
          sectionIdText="Step 2"
          title="Purchase individual showing-request leads"
          description="Each lead originates from real Request a Showing activity on REALTOR.ca — someone chose a listing and is waiting for an agent. You buy that assignment individually; we don't batch junk traffic or recycle stale lists."
          subtitle="Shared or exclusive"
          items={[
            'Shared leads may go to up to two (2) agents; exclusive leads to one (1) agent only — see FAQ',
            'Premium (fresh) and Clearance (aged) pools — buy what fits your budget',
            'Different from generic ad brokers — intent starts on REALTOR.ca, not a popup'
          ]}
          image="/images/img-05.png"
          imageAlt="Lead purchase and assignment"
          imageFirst={true}
          buttonText="View pricing"
          buttonLink="/pricing"
        />
        <ContentSection01
          sectionId="lnk-3"
          sectionIdText="Step 3"
          title="Contact fast — intent is highest now"
          description="Showing-request leads behave like inbound calls: speed and professionalism win. Follow up immediately while the prospect still expects your outreach."
          subtitle="Operational habit"
          items={[
            'Reply in minutes, not days — same discipline as a portal lead calling your cell',
            'Track outcomes in your CRM; conversion still depends on your process',
            'Optional mere postings (FSBO) may also be available depending on plan and brokerage rules'
          ]}
          image="/images/img-02.png"
          imageAlt="Fast follow-up on leads"
          imageFirst={false}
        />
        <ContentSection02
          image="/images/img-05.png"
          imageAlt="Shared vs exclusive lead distribution"
          textBoxes={[
            {
              title: 'Why REALTOR.ca showing requests beat popup leads',
              description:
                'Popup forms, forced registrations, and cold ad clicks often catch people earlier in the funnel — or accidentally. A Request a Showing is an intentional next step on a listing the prospect already cares about.'
            },
            {
              title: 'Shared vs exclusive delivery',
              description:
                'We offer both: shared leads are distributed to up to two (2) agents; exclusive leads are distributed to one (1) agent only. Pick the option that fits your speed-to-contact and budget.'
            }
          ]}
        />
        <TryItFree />
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorksPage;
